import {
  calculateSidePots,
  calculateUncalledBet,
  returnUncalledBet,
} from './pokerLogic';

/**
 * Side pots on screen should mean someone is all-in for less — not
 * "the blinds aren't matched yet", not "one player folded preflop",
 * and not a leftover contribution from a cashed-out player.
 *
 * calculateSidePots splits on every contribution level, so SB $1 / BB $2
 * — or a raise that is not yet called — looks like "2 POTS" the moment an
 * action writes pots. Uncalled chips are not a pot (see returnUncalledBet).
 * Collapse those for display unless a player is actually all-in for less.
 *
 * Do not key this off `isAllIn` alone. A stale flag on the short blind
 * (or the big blind) would keep unmatched blinds looking like a side pot
 * after a fold. A zero stack at a lower matched contribution level is
 * the real signal.
 */

function asPotList(pots) {
  if (Array.isArray(pots)) return pots.filter((p) => p && (p.amount || 0) >= 0);
  if (pots && typeof pots === 'object') {
    return Object.keys(pots)
      .sort()
      .map((k) => pots[k])
      .filter((p) => p && (p.amount || 0) >= 0);
  }
  return [];
}

function eligibleIds(pot) {
  const raw = pot?.eligiblePlayers;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    return Object.keys(raw).sort().map((k) => raw[k]);
  }
  return [];
}

function liveContributors(players) {
  return (players || []).filter((p) => {
    if ((p.totalPotContribution || 0) <= 0) return false;
    if (p.status === 'left') return false;
    return true;
  });
}

/** Called/matched money only: drop the unique unmatched top of the wager. */
export function matchedContributions(players) {
  const contributors = liveContributors(players);
  const uncalled = calculateUncalledBet(contributors);
  return contributors.map((p) => {
    let amount = p.totalPotContribution || 0;
    if (uncalled && p.id === uncalled.playerId) amount -= uncalled.amount;
    return { id: p.id, amount, stack: p.stack || 0 };
  }).filter((c) => c.amount > 0);
}

/**
 * True only when someone is actually all-in (no chips left) and at least
 * one other player has put in more than that all-in. Uneven bets mid-street
 * — SB still short, a raise not yet called — are one pot, not "2 POTS".
 *
 * Do not key this off `isAllIn` alone. A stale flag on a player who still
 * has a stack would keep unmatched blinds looking like a side pot.
 */
export function hasGenuineSidePots(players) {
  const matched = matchedContributions(players);
  if (new Set(matched.map((c) => c.amount)).size <= 1) return false;

  const maxMatched = Math.max(...matched.map((c) => c.amount));
  return matched.some((c) => c.stack === 0 && c.amount < maxMatched);
}

function collapseToOne(pots, players) {
  const list = asPotList(pots);
  const amount = list.length > 0
    ? list.reduce((sum, p) => sum + (p.amount || 0), 0)
    : (players || []).reduce((sum, p) => sum + (p.totalPotContribution || 0), 0);
  return [{
    id: 'main',
    amount,
    eligiblePlayers: eligibleIds(list[0]),
    isMainPot: true,
  }];
}

export function potsForDisplay(pots, players) {
  const list = asPotList(pots);
  if (!hasGenuineSidePots(players)) {
    if (list.length <= 1) return list;
    return collapseToOne(list, players);
  }

  const uncalled = calculateUncalledBet(liveContributors(players));
  if (!uncalled) return list;

  const stripped = list.filter((p) => {
    const ids = eligibleIds(p);
    const isUncalledLayer =
      !p.isMainPot &&
      ids.length === 1 &&
      ids[0] === uncalled.playerId &&
      Number(p.amount) === Number(uncalled.amount);
    return !isUncalledLayer;
  });
  return stripped.length > 0 ? stripped : list;
}

/**
 * Pots the host can actually award. Uncalled chips go back first so they
 * cannot be paid as a "side pot" or swallowed into a collapsed display pot.
 */
export function potsForAward(players) {
  const { players: settled, refund } = returnUncalledBet(players || []);
  return {
    pots: calculateSidePots(settled),
    players: settled,
    refund,
  };
}

/** Clear street/hand chips on a parked (cashed-out or removed) player. */
export function clearParkedHandChips(player) {
  if (!player) return player;
  return {
    ...player,
    currentRoundBet: 0,
    totalPotContribution: 0,
    isAllIn: false,
  };
}
